<template>
    <AppDialog v-model="isOpen" size="xl" fixed-width @close="resetAll">
        <template #header>
            <h2 class="text-lg font-semibold text-theme-base">
                {{ modalTitle }}
            </h2>
            <p v-if="step === 'type'" class="mt-1 text-sm text-theme-muted">
                Elige el tipo de bloque y un estilo para tu catálogo.
            </p>
        </template>

        <div
            v-if="step === 'type'"
            class="flex flex-col gap-4 sm:flex-row sm:items-start"
        >
            <div
                v-if="hasMultipleTypes"
                class="grid grid-cols-3 gap-2 sm:hidden"
            >
                <button
                    v-for="card in menuItems"
                    v-show="!card.scope || $ability.hasPermission(card.scope)"
                    :key="card.cardKey || card.id"
                    type="button"
                    class="flex flex-col items-center gap-1 rounded-xl border p-2 text-center text-xs font-medium transition"
                    :class="
                        activeMenuId === card.id
                            ? 'border-theme-primary bg-theme-hover text-theme-primary'
                            : 'border-transparent text-theme-muted hover:bg-theme-hover'
                    "
                    @click="selectMenuItem(card)"
                >
                    <span class="text-xl">{{ card.icon || "🔍" }}</span>
                    {{ card.name }}
                </button>
            </div>

            <div
                v-if="hasMultipleTypes"
                class="sticky top-0 hidden w-44 flex-shrink-0 space-y-1.5 overflow-y-auto border-r border-theme-border pr-3 sm:block sm:w-52 sm:max-h-110"
            >
                <button
                    v-for="card in menuItems"
                    v-show="!card.scope || $ability.hasPermission(card.scope)"
                    :key="card.cardKey || card.id"
                    type="button"
                    class="flex w-full items-start gap-2.5 rounded-xl border p-2.5 text-left transition"
                    :class="
                        activeMenuId === card.id
                            ? 'border-theme-primary bg-theme-hover'
                            : 'border-transparent hover:bg-theme-hover'
                    "
                    @click="selectMenuItem(card)"
                >
                    <span
                        class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-base"
                        :class="
                            activeMenuId === card.id
                                ? 'bg-theme-primary text-white'
                                : 'bg-theme-hover'
                        "
                    >
                        {{ card.icon || "🔍" }}
                    </span>
                    <span class="min-w-0 flex-1">
                        <span
                            class="block text-sm font-medium"
                            :class="
                                activeMenuId === card.id
                                    ? 'text-theme-primary'
                                    : 'text-theme-base'
                            "
                        >
                            {{ card.name }}
                        </span>
                        <span
                            v-if="card.description"
                            class="mt-0.5 block text-xs leading-snug text-theme-muted"
                        >
                            {{ card.description }}
                        </span>
                    </span>
                </button>
            </div>

            <div
                class="grid max-h-105 flex-1 grid-cols-1 gap-3 self-start overflow-y-auto pr-1 sm:max-h-110 sm:grid-cols-2 xl:grid-cols-3"
            >
                <template v-if="previewType === 'media'">
                    <button
                        v-for="option in socialPlatformOptions"
                        :key="option.value"
                        type="button"
                        class="flex h-32 flex-col items-center justify-center gap-2 self-start rounded-xl border border-theme-border p-4 text-center transition hover:border-theme-primary hover:shadow-sm"
                        @click="chooseSocialPlatform(option.value)"
                    >
                        <img
                            :src="platformIcon(option.value)"
                            :alt="option.label"
                            class="h-10 w-10 object-contain"
                        />
                        <span class="text-sm font-medium text-theme-base">
                            {{ option.label }}
                        </span>
                    </button>
                </template>

                <template v-else-if="previewType === uiGroupId">
                    <button
                        v-for="card in uiGroupCards"
                        :key="card.cardKey || card.id"
                        type="button"
                        class="flex h-32 flex-col items-center justify-center gap-2 self-start rounded-xl border border-theme-border p-4 text-center transition hover:border-theme-primary hover:shadow-sm"
                        @click="selectTypeCard(card)"
                    >
                        <span class="text-3xl">{{ card.icon }}</span>
                        <span class="text-sm font-medium text-theme-base">
                            {{ card.name }}
                        </span>
                        <span class="text-xs leading-snug text-theme-muted">
                            {{ card.description }}
                        </span>
                    </button>
                </template>

                <template v-else>
                    <div
                        v-if="isBannerStylePicker"
                        class="col-span-full flex flex-wrap items-center gap-2"
                    >
                        <span class="text-xs text-theme-muted">
                            Ver ejemplo con:
                        </span>
                        <div
                            class="flex items-center rounded-lg border border-theme-border p-1"
                        >
                            <button
                                v-for="mode in bannerSampleModeOptions"
                                :key="mode.value"
                                type="button"
                                class="rounded-md px-3 py-1.5 text-xs font-medium transition"
                                :class="
                                    bannerSampleMode === mode.value
                                        ? 'bg-theme-primary text-theme-inverse'
                                        : 'text-theme-muted hover:bg-theme-hover'
                                "
                                @click="bannerSampleMode = mode.value"
                            >
                                {{ mode.label }}
                            </button>
                        </div>
                    </div>
                    <div
                        v-for="section in styleOptionSections"
                        :key="section.key"
                        class="col-span-full"
                    >
                        <h3
                            v-if="section.label"
                            class="mb-2 text-xs font-semibold uppercase tracking-wide text-theme-muted"
                        >
                            {{ section.label }}
                        </h3>
                        <div
                            class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
                        >
                            <div
                                v-for="(column, colIndex) in section.columns"
                                :key="colIndex"
                                class="flex w-80vw min-w-0 flex-col gap-3 sm:w-auto"
                            >
                                <button
                                    v-for="option in column"
                                    :key="option.value"
                                    type="button"
                                    class="flex flex-col overflow-hidden rounded-xl border border-theme-border text-left transition hover:border-theme-primary hover:shadow-sm"
                                    @click="
                                        chooseTypeAndStyle(
                                            previewType,
                                            option.value
                                        )
                                    "
                                >
                                    <span
                                        class="flex min-h-segment-label items-center border-b border-theme-border px-3 py-2.5 text-sm font-semibold leading-tight text-theme-base"
                                    >
                                        <span class="line-clamp-2">{{
                                            option.label
                                        }}</span>
                                    </span>
                                    <div
                                        v-if="isBannerStylePicker"
                                        class="pointer-events-none flex justify-center overflow-hidden bg-theme-card p-4"
                                    >
                                        <PhoneFramePreview>
                                            <component
                                                :is="previewComponentName"
                                                v-bind="
                                                    previewPropsFor(
                                                        option.value
                                                    )
                                                "
                                                :variant="option.value"
                                                :is-desktop="false"
                                                preview-only
                                                class="w-full"
                                            />
                                        </PhoneFramePreview>
                                    </div>
                                    <div
                                        v-else
                                        class="pointer-events-none flex items-center justify-center overflow-hidden bg-stack-background p-4"
                                    >
                                        <component
                                            :is="previewComponentName"
                                            v-bind="
                                                previewPropsFor(option.value)
                                            "
                                            :variant="option.value"
                                            :is-desktop="false"
                                            preview-only
                                            class="w-full"
                                        />
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>

        <div v-else-if="selectedType === 'links' || selectedType === 'banners'">
            <div
                v-if="editingSegment"
                class="mb-4 flex gap-4 border-b border-theme-border"
            >
                <button
                    type="button"
                    class="flex items-center gap-1.5 border-b-2 px-1 pb-2 text-sm font-medium transition"
                    :class="
                        contentTab === 'content'
                            ? 'border-theme-primary text-theme-primary'
                            : 'border-transparent text-theme-muted hover:text-theme-base'
                    "
                    @click="contentTab = 'content'"
                >
                    {{ contentTabLabel }}
                </button>
                <button
                    type="button"
                    class="flex items-center gap-1.5 border-b-2 px-1 pb-2 text-sm font-medium transition"
                    :class="
                        contentTab === 'style'
                            ? 'border-theme-primary text-theme-primary'
                            : 'border-transparent text-theme-muted hover:text-theme-base'
                    "
                    @click="contentTab = 'style'"
                >
                    Estilo
                </button>
            </div>

            <div v-if="!editingSegment || contentTab === 'content'">
                <p v-if="isLoadingLinks" class="text-sm text-theme-muted">
                    Cargando...
                </p>
                <p
                    v-else-if="!existingLinks.length"
                    class="text-sm text-theme-muted"
                >
                    {{ emptyLinksMessage }}
                </p>
                <div v-else>
                    <p
                        v-if="selectedLinks.length > 1"
                        class="mb-2 text-xs text-theme-muted"
                    >
                        Arrastra los seleccionados para reordenarlos
                    </p>
                    <div class="max-h-96 space-y-2 overflow-y-auto pr-1">
                        <VueDraggable
                            v-model="selectedLinks"
                            tag="div"
                            handle=".drag-handle"
                            animation="200"
                            class="space-y-2"
                            @end="onSelectedLinkDragEnd"
                        >
                            <label
                                v-for="(link, index) in selectedLinks"
                                :key="link.id"
                                class="flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors duration-300"
                                :class="
                                    recentlyMovedLinkId === link.id
                                        ? 'border-theme-primary bg-theme-hover'
                                        : 'border-theme-border bg-theme-card hover:bg-theme-hover'
                                "
                            >
                                <custom-checkbox
                                    :value="selectedLinkIds"
                                    :val="link.id"
                                    @input="onToggleExistingLink(link, $event)"
                                />
                                <img
                                    v-if="link.imageUrl"
                                    :src="link.imageUrl"
                                    :alt="link.title"
                                    class="h-10 w-10 flex-shrink-0 rounded object-cover"
                                />
                                <span class="min-w-0 flex-1">
                                    <span
                                        class="block truncate text-sm text-theme-base"
                                    >
                                        {{ link.title }}
                                    </span>
                                    <span
                                        class="mt-0.5 inline-flex items-center gap-1 text-xs font-medium"
                                        :class="
                                            link.url
                                                ? 'text-theme-primary'
                                                : 'text-brand-red'
                                        "
                                    >
                                        <link-icon class="h-3 w-3" />
                                        {{
                                            link.url
                                                ? "Con enlace"
                                                : "Sin enlace"
                                        }}
                                    </span>
                                </span>
                                <button
                                    v-if="
                                        $ability.hasPermission(
                                            'customLink:Update'
                                        )
                                    "
                                    type="button"
                                    aria-label="Editar"
                                    class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-theme-muted transition hover:bg-theme-hover hover:text-theme-primary"
                                    @click="openEditLink(link)"
                                >
                                    <pencil-icon class="h-3.5 w-3.5" />
                                </button>
                                <span
                                    v-if="selectedLinks.length > 1"
                                    class="drag-handle flex-shrink-0 cursor-grab select-none px-1 text-theme-muted"
                                >
                                    ⠿
                                </span>
                                <span
                                    class="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-theme-hover text-xs font-semibold text-theme-base"
                                >
                                    {{ index + 1 }}
                                </span>
                            </label>
                        </VueDraggable>

                        <label
                            v-for="link in unselectedLinks"
                            :key="link.id"
                            class="flex items-center gap-3 rounded-lg border border-theme-border p-3 cursor-pointer hover:bg-theme-hover"
                        >
                            <custom-checkbox
                                :value="selectedLinkIds"
                                :val="link.id"
                                @input="onToggleExistingLink(link, $event)"
                            />
                            <img
                                v-if="link.imageUrl"
                                :src="link.imageUrl"
                                :alt="link.title"
                                class="h-10 w-10 flex-shrink-0 rounded object-cover"
                            />
                            <span class="min-w-0 flex-1">
                                <span
                                    class="block truncate text-sm text-theme-base"
                                >
                                    {{ link.title }}
                                </span>
                                <span
                                    class="mt-0.5 inline-flex items-center gap-1 text-xs font-medium"
                                    :class="
                                        link.url
                                            ? 'text-theme-primary'
                                            : 'text-brand-red'
                                    "
                                >
                                    <link-icon class="h-3 w-3" />
                                    {{ link.url ? "Con enlace" : "Sin enlace" }}
                                </span>
                            </span>
                            <button
                                v-if="
                                    $ability.hasPermission('customLink:Update')
                                "
                                type="button"
                                aria-label="Editar"
                                class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-theme-muted transition hover:bg-theme-hover hover:text-theme-primary"
                                @click="openEditLink(link)"
                            >
                                <pencil-icon class="h-3.5 w-3.5" />
                            </button>
                            <span class="flex-shrink-0 px-1 text-theme-muted">
                                —
                            </span>
                        </label>
                    </div>
                </div>

                <button
                    v-if="canCreateLinkForSelectedType"
                    type="button"
                    class="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-theme-border px-3 py-2 text-sm font-medium text-theme-muted transition hover:border-theme-primary hover:text-theme-primary"
                    @click="openCreateLink()"
                >
                    <plus-icon class="h-4 w-4" />
                    {{
                        selectedType === "banners"
                            ? "Crear nuevo banner"
                            : "Crear nuevo enlace"
                    }}
                </button>

                <create-custom-link-modal
                    v-model="createCustomLinkModalOpen"
                    :segment-type="selectedType"
                    :link="editingLink"
                    @created="onCustomLinkCreated"
                    @updated="onCustomLinkUpdated"
                />
            </div>
        </div>

        <div v-else-if="selectedType === 'productsCarousel'">
            <div
                v-if="editingSegment"
                class="mb-4 flex gap-4 border-b border-theme-border"
            >
                <button
                    type="button"
                    class="flex items-center gap-1.5 border-b-2 px-1 pb-2 text-sm font-medium transition"
                    :class="
                        contentTab === 'content'
                            ? 'border-theme-primary text-theme-primary'
                            : 'border-transparent text-theme-muted hover:text-theme-base'
                    "
                    @click="contentTab = 'content'"
                >
                    {{ contentTabLabel }}
                </button>
                <button
                    type="button"
                    class="flex items-center gap-1.5 border-b-2 px-1 pb-2 text-sm font-medium transition"
                    :class="
                        contentTab === 'style'
                            ? 'border-theme-primary text-theme-primary'
                            : 'border-transparent text-theme-muted hover:text-theme-base'
                    "
                    @click="contentTab = 'style'"
                >
                    Estilo
                </button>
            </div>

            <div v-if="!editingSegment || contentTab === 'content'">
                <products-search
                    paginated
                    :item-current="{ name: '' }"
                    show-suggestions
                    @input="onProductSelected"
                />

                <button
                    v-if="$ability.hasPermission('product:Create')"
                    type="button"
                    class="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-theme-border px-3 py-2 text-sm font-medium text-theme-muted transition hover:border-theme-primary hover:text-theme-primary"
                    @click="createProductModalOpen = true"
                >
                    <plus-icon class="h-4 w-4" />
                    Crear nuevo producto
                </button>

                <create-product-modal
                    v-model="createProductModalOpen"
                    publish-only
                    @created="onProductCreated"
                />

                <div
                    v-if="selectedProducts.length"
                    class="mt-4 border-t border-theme-border pt-3"
                >
                    <p class="text-xs text-theme-muted mb-2">
                        Seleccionados ({{ selectedProducts.length }})
                        <template v-if="selectedProducts.length > 1">
                            · Arrastra para reordenarlos
                        </template>
                    </p>
                    <div class="max-h-96 overflow-y-auto pr-1">
                        <VueDraggable
                            v-model="selectedProducts"
                            tag="div"
                            handle=".drag-handle"
                            animation="200"
                            class="space-y-2"
                            @end="onSelectedProductDragEnd"
                        >
                            <div
                                v-for="(product, index) in selectedProducts"
                                :key="product.id"
                                class="flex items-center gap-3 rounded-lg border p-3 transition-colors duration-300"
                                :class="
                                    recentlyMovedProductId === product.id
                                        ? 'border-theme-primary bg-theme-hover'
                                        : 'border-theme-border bg-theme-card'
                                "
                            >
                                <span
                                    v-if="selectedProducts.length > 1"
                                    class="drag-handle flex-shrink-0 cursor-grab select-none px-1 text-theme-muted"
                                >
                                    ⠿
                                </span>
                                <span
                                    class="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-theme-hover text-xs font-semibold text-theme-base"
                                >
                                    {{ index + 1 }}
                                </span>
                                <img
                                    v-if="productImageUrl(product)"
                                    :src="productImageUrl(product)"
                                    :alt="product.name"
                                    class="h-10 w-10 flex-shrink-0 rounded object-cover"
                                />
                                <span
                                    class="min-w-0 flex-1 truncate text-sm text-theme-base"
                                >
                                    {{ product.name }}
                                </span>
                                <button
                                    type="button"
                                    aria-label="Quitar producto"
                                    class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-theme-muted transition hover:bg-theme-danger hover:text-white"
                                    @click="removeSelectedProduct(product)"
                                >
                                    <trash-icon class="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </VueDraggable>
                    </div>
                </div>
            </div>
        </div>

        <div v-else-if="selectedType === 'text'">
            <div
                v-if="editingSegment"
                class="mb-4 flex gap-4 border-b border-theme-border"
            >
                <button
                    type="button"
                    class="flex items-center gap-1.5 border-b-2 px-1 pb-2 text-sm font-medium transition"
                    :class="
                        contentTab === 'content'
                            ? 'border-theme-primary text-theme-primary'
                            : 'border-transparent text-theme-muted hover:text-theme-base'
                    "
                    @click="contentTab = 'content'"
                >
                    {{ contentTabLabel }}
                </button>
                <button
                    type="button"
                    class="flex items-center gap-1.5 border-b-2 px-1 pb-2 text-sm font-medium transition"
                    :class="
                        contentTab === 'style'
                            ? 'border-theme-primary text-theme-primary'
                            : 'border-transparent text-theme-muted hover:text-theme-base'
                    "
                    @click="contentTab = 'style'"
                >
                    Estilo
                </button>
            </div>

            <div v-if="!editingSegment || contentTab === 'content'">
                <div
                    class="flex flex-wrap items-center gap-1.5 rounded-lg border border-theme-border bg-theme-card p-1.5"
                >
                    <custom-simple-select
                        v-model="selectedFontFamily"
                        compact
                        :options="fontOptions"
                        placeholder="Predeterminada"
                    >
                        <template #prefix>
                            <span
                                class="font-serif text-sm font-semibold leading-none text-theme-muted"
                            >
                                Aa
                            </span>
                        </template>
                    </custom-simple-select>
                </div>

                <div class="mt-3 space-y-3">
                    <custom-input
                        v-model="textTitle"
                        label="Título (opcional)"
                        placeholder="Escribe un título"
                    />

                    <div>
                        <label class="mb-1 block text-sm text-theme-muted">
                            Texto (opcional)
                        </label>
                        <textarea
                            v-model="textBody"
                            v-autogrow
                            rows="3"
                            placeholder="Escribe el texto…"
                            class="w-full resize-none overflow-hidden rounded-md border border-theme-border bg-theme-input px-3 py-2 text-sm text-theme-base placeholder-theme-muted focus:outline-none focus:ring-1 focus:ring-theme-primary"
                        ></textarea>
                    </div>

                    <div v-if="selectedStyle === 'titleImage'">
                        <label class="mb-2 block text-sm text-theme-muted">
                            Imagen *
                        </label>
                        <image-manage
                            v-model="textImage"
                            :enabled-create="true"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div v-else-if="selectedType === 'testimonials'">
            <p
                v-if="testimonialItems.length > 1"
                class="mb-2 text-xs text-theme-muted"
            >
                Arrastra los testimonios para reordenarlos
            </p>
            <VueDraggable
                v-model="testimonialItems"
                tag="div"
                handle=".drag-handle"
                animation="200"
                class="space-y-2"
            >
                <div
                    v-for="(item, index) in testimonialItems"
                    :key="item.id"
                    class="flex items-start gap-3 rounded-lg border border-theme-border p-3"
                >
                    <span
                        v-if="testimonialItems.length > 1"
                        class="drag-handle mt-2 flex-shrink-0 cursor-grab select-none text-theme-muted"
                    >
                        ⠿
                    </span>
                    <span
                        class="mt-2 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-theme-hover text-xs font-semibold text-theme-base"
                    >
                        {{ index + 1 }}
                    </span>
                    <div class="w-30 flex-shrink-0">
                        <image-manage
                            v-model="item.image"
                            :enabled-create="true"
                        />
                    </div>
                    <div class="min-w-0 flex-1 space-y-2">
                        <custom-input
                            v-model="item.name"
                            placeholder="Nombre"
                        />
                        <textarea
                            v-model="item.quote"
                            v-autogrow
                            rows="2"
                            placeholder="Escribe la cita…"
                            class="w-full resize-none overflow-hidden rounded-md border border-theme-border bg-theme-input px-3 py-2 text-sm italic text-theme-base placeholder-theme-muted focus:outline-none focus:ring-1 focus:ring-theme-primary"
                        ></textarea>
                    </div>
                    <button
                        type="button"
                        aria-label="Eliminar testimonio"
                        class="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-theme-muted transition hover:bg-theme-danger hover:text-white"
                        @click="removeTestimonialItem(index)"
                    >
                        <trash-icon class="h-3.5 w-3.5" />
                    </button>
                </div>
            </VueDraggable>

            <button
                type="button"
                class="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-theme-border px-3 py-2 text-sm font-medium text-theme-muted transition hover:border-theme-primary hover:text-theme-primary"
                @click="addTestimonialItem"
            >
                <plus-icon class="h-4 w-4" />
                Agregar testimonio
            </button>
        </div>

        <div v-else-if="selectedType === 'media'">
            <div v-if="socialSupportsMultiple" class="flex gap-2">
                <button
                    v-for="option in socialDisplayModeOptions"
                    :key="option.value"
                    type="button"
                    class="flex-1 rounded-lg border px-3 py-1.5 text-sm font-medium transition"
                    :class="
                        socialDisplayMode === option.value
                            ? 'border-theme-primary bg-theme-hover text-theme-primary'
                            : 'border-theme-border text-theme-muted'
                    "
                    @click="selectSocialDisplayMode(option.value)"
                >
                    {{ option.label }}
                </button>
            </div>

            <template
                v-if="!socialSupportsMultiple || socialDisplayMode === 'single'"
            >
                <custom-input
                    v-model="socialUrlInput"
                    :placeholder="socialUrlPlaceholder"
                    :error="socialUrlError"
                    class="mt-3"
                />

                <div v-if="socialEmbedValid" class="mt-4">
                    <media-segment
                        :key="socialUrlInput"
                        :media-type="socialPlatformInput"
                        :link="socialUrlInput"
                        :colors="colors"
                    />
                </div>
            </template>

            <template v-else>
                <p
                    v-if="socialItems.length > 1"
                    class="mb-2 mt-3 text-xs text-theme-muted"
                >
                    Arrastra las publicaciones para reordenarlas
                </p>
                <VueDraggable
                    v-model="socialItems"
                    tag="div"
                    handle=".drag-handle"
                    animation="200"
                    class="space-y-2"
                    :class="{ 'mt-3': socialItems.length <= 1 }"
                >
                    <div
                        v-for="(item, index) in socialItems"
                        :key="item.id"
                        class="flex items-start gap-2 rounded-lg border border-theme-border p-3"
                    >
                        <span
                            v-if="socialItems.length > 1"
                            class="drag-handle mt-2 flex-shrink-0 cursor-grab select-none text-theme-muted"
                        >
                            ⠿
                        </span>
                        <span
                            class="mt-2 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-theme-hover text-xs font-semibold text-theme-base"
                        >
                            {{ index + 1 }}
                        </span>
                        <div class="min-w-0 flex-1">
                            <custom-input
                                v-model="item.link"
                                :placeholder="socialUrlPlaceholder"
                                :error="socialItemError(item)"
                                @blur="onSocialItemLinkBlur(item)"
                            />
                            <p
                                v-if="item.thumbnailLoading"
                                class="mt-1 text-xs text-theme-muted"
                            >
                                Buscando miniatura…
                            </p>
                        </div>
                        <button
                            type="button"
                            aria-label="Eliminar publicación"
                            class="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-theme-muted transition hover:bg-theme-danger hover:text-white"
                            @click="removeSocialItem(index)"
                        >
                            <trash-icon class="h-3.5 w-3.5" />
                        </button>
                    </div>
                </VueDraggable>

                <button
                    type="button"
                    class="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-theme-border px-3 py-2 text-sm font-medium text-theme-muted transition hover:border-theme-primary hover:text-theme-primary"
                    @click="addSocialItem"
                >
                    <plus-icon class="h-4 w-4" />
                    Agregar publicación
                </button>

                <div class="mt-4">
                    <social-feed-segment
                        :media-type="socialPlatformInput"
                        :items="socialItems"
                        :colors="colors"
                        preview-only
                    />
                </div>
            </template>
        </div>

        <div v-else-if="selectedType === 'markdown'">
            <markdown-segment-editor v-model="markdownContent" />
        </div>

        <div v-else-if="selectedType === 'divider'" class="space-y-4">
            <div>
                <label class="mb-2 block text-sm text-theme-muted">
                    Estilo
                </label>
                <div class="flex gap-2">
                    <button
                        v-for="option in dividerStyleOptions"
                        :key="option.value"
                        type="button"
                        class="flex-1 rounded-lg border px-3 py-1.5 text-sm font-medium transition"
                        :class="
                            selectedStyle === option.value
                                ? 'border-theme-primary bg-theme-hover text-theme-primary'
                                : 'border-theme-border text-theme-muted'
                        "
                        @click="selectedStyle = option.value"
                    >
                        {{ option.label }}
                    </button>
                </div>
            </div>

            <div>
                <label class="mb-2 block text-sm text-theme-muted">
                    Tamaño
                </label>
                <div class="flex gap-2">
                    <button
                        v-for="option in dividerSizeOptions"
                        :key="option.value"
                        type="button"
                        class="flex-1 rounded-lg border px-3 py-1.5 text-sm font-medium transition"
                        :class="
                            dividerSize === option.value
                                ? 'border-theme-primary bg-theme-hover text-theme-primary'
                                : 'border-theme-border text-theme-muted'
                        "
                        @click="dividerSize = option.value"
                    >
                        {{ option.label }}
                    </button>
                </div>
            </div>
        </div>

        <div
            v-if="
                step === 'config' &&
                editingSegment &&
                styleOptions.length &&
                contentTab === 'style'
            "
        >
            <div
                v-if="isBannerStylePicker"
                class="mb-3 flex flex-wrap items-center gap-2"
            >
                <span class="text-xs text-theme-muted"> Ver ejemplo con: </span>
                <div
                    class="flex items-center rounded-lg border border-theme-border p-1"
                >
                    <button
                        v-for="mode in bannerSampleModeOptions"
                        :key="mode.value"
                        type="button"
                        class="rounded-md px-3 py-1.5 text-xs font-medium transition"
                        :class="
                            bannerSampleMode === mode.value
                                ? 'bg-theme-primary text-theme-inverse'
                                : 'text-theme-muted hover:bg-theme-hover'
                        "
                        @click="bannerSampleMode = mode.value"
                    >
                        {{ mode.label }}
                    </button>
                </div>
            </div>
            <div
                v-for="section in orderedStyleOptionSections"
                :key="section.key"
                class="mb-4"
            >
                <h3
                    v-if="section.label"
                    class="mb-2 text-xs font-semibold uppercase tracking-wide text-theme-muted"
                >
                    {{ section.label }}
                </h3>
                <div
                    class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
                >
                    <div
                        v-for="(column, colIndex) in section.columns"
                        :key="colIndex"
                        class="flex w-80vw min-w-0 flex-col gap-3 sm:w-auto"
                    >
                        <button
                            v-for="option in column"
                            :key="option.value"
                            type="button"
                            class="relative flex flex-col overflow-hidden rounded-xl border-2 text-left transition"
                            :class="
                                selectedStyle === option.value
                                    ? 'border-theme-primary bg-theme-hover ring-2 ring-theme-primary ring-offset-2'
                                    : 'border-theme-border hover:border-theme-primary hover:shadow-sm'
                            "
                            @click="selectedStyle = option.value"
                        >
                            <span
                                class="flex min-h-segment-label items-center gap-1 border-b border-theme-border px-3 py-2.5 text-sm font-semibold leading-tight"
                                :class="
                                    selectedStyle === option.value
                                        ? 'text-theme-primary'
                                        : 'text-theme-base'
                                "
                            >
                                <span class="line-clamp-2">{{
                                    option.label
                                }}</span>
                            </span>
                            <div
                                class="relative flex items-center justify-center overflow-hidden p-4"
                                :class="
                                    isBannerStylePicker
                                        ? 'bg-theme-card'
                                        : 'bg-stack-background'
                                "
                            >
                                <span
                                    v-if="selectedStyle === option.value"
                                    class="absolute right-2 top-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-theme-primary text-white shadow-sm"
                                >
                                    <check-icon class="h-3 w-3" />
                                </span>
                                <PhoneFramePreview
                                    v-if="isBannerStylePicker"
                                    class="pointer-events-none"
                                >
                                    <component
                                        :is="previewComponentName"
                                        v-bind="previewPropsFor(option.value)"
                                        :variant="option.value"
                                        :is-desktop="false"
                                        preview-only
                                        class="w-full"
                                    />
                                </PhoneFramePreview>
                                <component
                                    :is="previewComponentName"
                                    v-else
                                    v-bind="previewPropsFor(option.value)"
                                    :variant="option.value"
                                    :is-desktop="false"
                                    preview-only
                                    class="pointer-events-none w-full"
                                />
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end gap-2">
                <button
                    class="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                    @click="isOpen = false"
                >
                    Cancelar
                </button>
                <button
                    v-if="step === 'config' && !editingSegment"
                    class="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                    @click="goBackToType"
                >
                    Volver
                </button>
                <app-button
                    v-if="step === 'config'"
                    color="success"
                    :disabled="!canConfirm"
                    @click="confirm"
                >
                    {{ editingSegment ? "Guardar cambios" : addSegmentLabel }}
                </app-button>
            </div>
        </template>
    </AppDialog>
</template>

<script>
import VueDraggable from "vuedraggable";
import { mapActions, mapGetters } from "vuex";

import { SOCIAL_NETWORK_OPTIONS } from "@/common/fields/social-networks";
import { getButtonStyle } from "@/common/lib/button-style";
import { isLinksOnlyBannerStyle } from "@/common/lib/links-only-segments";
import {
    SOCIAL_DISPLAY_MODE_OPTIONS,
    SOCIAL_PLATFORM_LABELS,
    SOCIAL_PLATFORM_OPTIONS,
    detectSocialEmbed,
    fetchInstagramThumbnail,
    fetchTiktokThumbnail,
    supportsMultipleDisplay
} from "@/common/social-embeds";
import { TEXT_SEGMENT_FONT_OPTIONS } from "@/common/text-segment-fonts";

import BannersSegment from "@/components/catalog/BannersSegment.vue";
import LinksSegment from "@/components/catalog/LinksSegment.vue";
import MediaSegment from "@/components/catalog/MediaSegment.vue";
import ProductsCarouselSegment from "@/components/catalog/ProductsCarouselSegment.vue";
import SocialFeedSegment from "@/components/catalog/SocialFeedSegment.vue";
import TestimonialsSegment from "@/components/catalog/TestimonialsSegment.vue";
import TextSegment from "@/components/catalog/TextSegment.vue";
import { productCarouselVariantOptions } from "@/components/catalog/product-sliders";
import {
    DEFAULT_SLIDER_VARIANT,
    SLIDER_SIZE_TIER_OPTIONS,
    sliderSizeTiers,
    sliderVariantOptions
} from "@/components/catalog/sliders";
import CreateCustomLinkModal from "@/components/editor/CreateCustomLinkModal.vue";
import CreateProductModal from "@/components/editor/CreateProductModal.vue";
import MarkdownSegmentEditor from "@/components/editor/MarkdownSegmentEditor.vue";
import PhoneFramePreview from "@/components/editor/PhoneFramePreview.vue";
import CheckIcon from "@/components/icons/CheckIcon.vue";
import LinkIcon from "@/components/icons/LinkIcon.vue";
import PencilIcon from "@/components/icons/PencilIcon.vue";
import PlusIcon from "@/components/icons/PlusIcon.vue";
import TrashIcon from "@/components/icons/TrashIcon.vue";
import ProductsSearch from "@/components/products/ProductsSearch.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import CustomCheckbox from "@/components/utils/CustomCheckbox.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import CustomSimpleSelect from "@/components/utils/CustomSimpleSelect.vue";
import ImageManage from "@/components/utils/ImageManage.vue";

const TYPE_CARDS = [
    {
        id: "links",
        name: "Link/Botón",
        icon: "🔗",
        description: "Un botón con texto e imagen chica opcional.",
        scope: "customLink:List"
    },
    {
        id: "banners",
        name: "Banners",
        icon: "🖼️",
        description: "Imagen a todo ancho, con o sin texto.",
        scope: "customLink:List"
    },
    {
        id: "productsCarousel",
        name: "Carrusel de productos",
        icon: "🛍️",
        description: "Una selección de productos de tu catálogo.",
        scope: "product:List"
    },
    {
        id: "media",
        name: "Redes sociales",
        icon: "📱",
        description: "Un video o publicación de YouTube, Instagram o TikTok."
    },
    {
        id: "testimonials",
        name: "Testimonios",
        icon: "💬",
        description: "Opiniones de tus clientes, en carrusel.",
        group: "ui"
    },
    {
        id: "text",
        name: "Bloque de texto",
        icon: "📝",
        description: "Títulos, párrafos y testimonios, apilados en columna.",
        group: "ui"
    },
    {
        id: "markdown",
        name: "Markdown",
        icon: "🧾",
        description:
            "Formato libre: encabezados, listas, negritas, enlaces y embeds.",
        group: "ui"
    },
    {
        id: "divider",
        name: "Espacio en blanco",
        icon: "⬜",
        description: "Deja aire entre bloques, sin línea visible.",
        group: "ui",
        presetStyle: "space",
        cardKey: "divider-space"
    },
    {
        id: "divider",
        name: "Divisor",
        icon: "➖",
        description: "Línea separadora entre bloques.",
        group: "ui",
        presetStyle: "line",
        cardKey: "divider-line"
    }
];

const UI_GROUP = {
    id: "ui",
    name: "Interfaz",
    icon: "🧩",
    description: "Texto, testimonios, markdown, espacios y divisores."
};

const TYPE_LABELS = {
    links: "Link/Botón",
    banners: "Banners",
    productsCarousel: "Carrusel de productos",
    text: "Bloque de texto",
    testimonials: "Testimonios",
    media: "Redes sociales",
    markdown: "Markdown",
    divider: "División"
};

const CONTENT_TAB_LABELS = {
    links: "Enlaces",
    banners: "Banners",
    productsCarousel: "Productos",
    text: "Texto"
};

export const STYLE_OPTIONS = {
    links: [
        { value: "block", label: "Botones largos" },
        { value: "grid", label: "Grid" }
    ],
    banners: sliderVariantOptions,
    productsCarousel: productCarouselVariantOptions,
    text: [
        { value: "titleText", label: "Título y texto" },
        { value: "titleCentered", label: "Título centrado" },
        { value: "titleImage", label: "Título e imagen" }
    ],
    testimonials: [{ value: "carousel", label: "Carrusel de testimonios" }],
    divider: [
        { value: "line", label: "Línea separadora" },
        { value: "space", label: "Solo espacio" }
    ]
};

const DIVIDER_SIZE_OPTIONS = [
    { value: "sm", label: "Pequeño" },
    { value: "md", label: "Mediano" },
    { value: "lg", label: "Grande" }
];

const PREVIEW_COMPONENTS = {
    links: "LinksSegment",
    banners: "BannersSegment",
    productsCarousel: "ProductsCarouselSegment",
    text: "TextSegment",
    testimonials: "TestimonialsSegment"
};

const BRAND_ICON_URL = "/img/logos/short_logo.png";

const SAMPLE_LINKS = [
    {
        id: "sample-1",
        url: "#",
        title: "Catálogo",
        imageUrl: BRAND_ICON_URL
    },
    {
        id: "sample-2",
        url: "#",
        title: "WhatsApp",
        imageUrl: null
    },
    {
        id: "sample-3",
        url: "#",
        title: "Info",
        imageUrl: BRAND_ICON_URL
    },
    {
        id: "sample-4",
        url: "#",
        title: "Promociones",
        imageUrl: null
    }
];

// Mismos 4 ejemplos para todos los estilos (con "1 imagen" se usa solo el
// primero), para comparar los estilos en la misma condición.
const SAMPLE_BANNERS = [
    {
        id: "sample-1",
        url: "#",
        title: "Nueva colección",
        imageUrl: "/img/default/banner_1.webp"
    },
    {
        id: "sample-2",
        url: "#",
        title: "Ofertas",
        imageUrl: "/img/default/banner_2.webp"
    },
    {
        id: "sample-3",
        url: "#",
        title: "Más vendidos",
        imageUrl: "/img/default/banner_3.webp"
    },
    {
        id: "sample-4",
        url: "#",
        title: "Contáctanos",
        imageUrl: "/img/default/banner_1.webp"
    }
];

const SAMPLE_PRODUCTS = [
    {
        slug: "sample-1",
        name: "Ejemplo",
        references: [{ images: [{ url: "/img/default/product_1.webp" }] }],
        price: 40000,
        priceOffer: 0
    }
];

const SAMPLE_TEXT_TITLE = "Nuestra historia";
const SAMPLE_TEXT_BODY = "Un párrafo de ejemplo para la vista previa.";
const SAMPLE_TEXT_IMAGE_URL = "/img/default/banner_1.webp";

const SAMPLE_TESTIMONIAL_ITEMS = [
    {
        id: "sample-1",
        name: "Camila Ruiz",
        quote: "Excelente atención y productos de calidad.",
        imageUrl: null
    },
    {
        id: "sample-2",
        name: "Juan Pérez",
        quote: "Siempre encuentro lo que busco, muy recomendado.",
        imageUrl: null
    }
];

const STYLE_OPTION_COLUMNS = { mobile: 1, tablet: 2, desktop: 3 };
const STYLE_OPTION_TABLET_BREAKPOINT = 640;
const STYLE_OPTION_DESKTOP_BREAKPOINT = 1280;

const BANNER_SAMPLE_MODE_OPTIONS = [
    { value: "one", label: "1 imagen" },
    { value: "many", label: "Varias" }
];

const generateSegmentItemId = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `item-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const createSocialItem = () => ({
    id: generateSegmentItemId(),
    link: "",
    thumbnailUrl: null,
    thumbnailLoading: false,
    fetchedForLink: ""
});

const createTestimonialItem = () => ({
    id: generateSegmentItemId(),
    name: "",
    quote: "",
    image: null
});

export default {
    name: "AddSegmentModal",
    components: {
        AppDialog,
        CustomCheckbox,
        CustomInput,
        CustomSimpleSelect,
        ImageManage,
        MarkdownSegmentEditor,
        ProductsSearch,
        CreateProductModal,
        CreateCustomLinkModal,
        CheckIcon,
        PhoneFramePreview,
        LinkIcon,
        PencilIcon,
        PlusIcon,
        TrashIcon,
        LinksSegment,
        BannersSegment,
        ProductsCarouselSegment,
        TextSegment,
        TestimonialsSegment,
        MediaSegment,
        SocialFeedSegment,
        VueDraggable
    },
    directives: {
        autogrow: {
            inserted(el) {
                el.style.height = "auto";
                el.style.height = `${el.scrollHeight}px`;
            },
            componentUpdated(el) {
                el.style.height = "auto";
                el.style.height = `${el.scrollHeight}px`;
            }
        }
    },
    model: {
        prop: "value",
        event: "input"
    },
    props: {
        value: {
            type: Boolean,
            default: false
        },
        stack: {
            type: Object,
            required: true
        },
        colors: {
            type: Object,
            required: true
        },
        dark: {
            type: Boolean,
            default: false
        },
        allowedTypes: {
            type: Array,
            default: null
        },
        isLinksOnlyMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            step: "type",
            selectedType: "",
            previewType:
                this.buildMenuItems(this.filterTypeCards(TYPE_CARDS))[0]?.id ||
                "",
            pendingStyle: null,
            isLoadingLinks: false,
            existingLinks: [],
            selectedLinks: [],
            contentTab: "content",
            createCustomLinkModalOpen: false,
            editingLink: null,
            selectedProducts: [],
            createProductModalOpen: false,
            textTitle: "",
            textBody: "",
            textImage: null,
            testimonialItems: [],
            markdownContent: "",
            dividerSize: "md",
            socialPlatformInput: SOCIAL_PLATFORM_OPTIONS[0].value,
            socialUrlInput: "",
            socialDisplayMode: "single",
            socialItems: [],
            selectedStyle: "",
            styleOrderAnchor: "",
            selectedFontFamily: null,
            editingSegment: null,
            skipNextTypeReset: false,
            recentlyMovedLinkId: null,
            recentlyMovedProductId: null,
            styleOptionColumnCount: STYLE_OPTION_COLUMNS.mobile,
            bannerSampleMode: "many"
        };
    },
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        isOpen: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit("input", value);
            }
        },
        typeCards() {
            return this.filterTypeCards(TYPE_CARDS);
        },
        uiGroupId() {
            return UI_GROUP.id;
        },
        uiGroupCards() {
            return this.typeCards.filter((card) => card.group === UI_GROUP.id);
        },
        menuItems() {
            return this.buildMenuItems(this.typeCards);
        },
        activeMenuId() {
            return this.uiGroupCards.some(
                (card) => card.id === this.previewType
            )
                ? UI_GROUP.id
                : this.previewType;
        },
        hasMultipleTypes() {
            return this.typeCards.length > 1;
        },
        typeLabel() {
            return TYPE_LABELS[this.selectedType] || "";
        },
        contentTabLabel() {
            return CONTENT_TAB_LABELS[this.selectedType] || this.typeLabel;
        },
        socialPlatformOptions() {
            return SOCIAL_PLATFORM_OPTIONS;
        },
        socialDisplayModeOptions() {
            return SOCIAL_DISPLAY_MODE_OPTIONS;
        },
        socialSupportsMultiple() {
            return supportsMultipleDisplay(this.socialPlatformInput);
        },
        socialPlatformLabel() {
            return SOCIAL_PLATFORM_LABELS[this.socialPlatformInput] || "";
        },
        socialUrlPlaceholder() {
            const label =
                SOCIAL_PLATFORM_LABELS[this.socialPlatformInput] || "";
            return `Pega el link de ${label}`;
        },
        socialDetectedEmbed() {
            return detectSocialEmbed(this.socialUrlInput);
        },
        socialEmbedValid() {
            return (
                !!this.socialDetectedEmbed &&
                this.socialDetectedEmbed.platform === this.socialPlatformInput
            );
        },
        socialUrlError() {
            if (!this.socialUrlInput.trim()) return "";
            const platformLabel =
                SOCIAL_PLATFORM_LABELS[this.socialPlatformInput];
            if (!this.socialDetectedEmbed) {
                return `Pega un enlace válido de ${platformLabel}`;
            }
            if (
                this.socialDetectedEmbed.platform !== this.socialPlatformInput
            ) {
                return `Ese enlace parece ser de ${
                    SOCIAL_PLATFORM_LABELS[this.socialDetectedEmbed.platform]
                }, no de ${platformLabel}`;
            }
            return "";
        },
        addSegmentLabel() {
            return "Agregar bloque";
        },
        modalTitle() {
            if (this.step === "type") return this.addSegmentLabel;
            return this.editingSegment
                ? `Editar: ${this.typeLabel}`
                : this.typeLabel;
        },
        selectedLinkIds() {
            return this.selectedLinks.map((link) => link.id);
        },
        unselectedLinks() {
            const selectedIds = new Set(this.selectedLinkIds);
            return this.existingLinks.filter(
                (link) => !selectedIds.has(link.id)
            );
        },
        activeType() {
            return this.step === "type" ? this.previewType : this.selectedType;
        },
        styleOptions() {
            const options = STYLE_OPTIONS[this.activeType] || [];
            if (this.isLinksOnlyMode && this.activeType === "banners") {
                return options.filter((option) =>
                    isLinksOnlyBannerStyle(option.value)
                );
            }
            return options;
        },
        emptyLinksMessage() {
            if (this.selectedType !== "banners") {
                return "Todavía no tienes enlaces de este tipo.";
            }
            return this.canCreateLinkForSelectedType
                ? "Todavía no tienes banners. Crea uno para agregarlo a este bloque."
                : "Todavía no tienes banners.";
        },
        canCreateLinkForSelectedType() {
            return this.$ability.hasPermission(
                this.selectedType === "banners"
                    ? "customLink:CreateBanner"
                    : "customLink:Create"
            );
        },
        fontOptions() {
            return TEXT_SEGMENT_FONT_OPTIONS;
        },
        dividerStyleOptions() {
            return STYLE_OPTIONS.divider;
        },
        dividerSizeOptions() {
            return DIVIDER_SIZE_OPTIONS;
        },
        orderedStyleOptions() {
            if (!this.editingSegment) return this.styleOptions;
            const options = this.styleOptions;
            const anchorIndex = options.findIndex(
                (option) => option.value === this.styleOrderAnchor
            );
            if (anchorIndex <= 0) return options;
            const anchor = options[anchorIndex];
            return [
                anchor,
                ...options.slice(0, anchorIndex),
                ...options.slice(anchorIndex + 1)
            ];
        },
        isBannerStylePicker() {
            return this.activeType === "banners";
        },
        bannerSampleModeOptions() {
            return BANNER_SAMPLE_MODE_OPTIONS;
        },
        styleOptionSections() {
            return this.buildStyleOptionSections(this.styleOptions);
        },
        orderedStyleOptionSections() {
            return this.buildStyleOptionSections(this.orderedStyleOptions);
        },
        previewComponentName() {
            return PREVIEW_COMPONENTS[this.activeType];
        },
        linkStyle() {
            return getButtonStyle("rounded", this.stack);
        },
        canConfirm() {
            if (
                this.selectedType === "links" ||
                this.selectedType === "banners"
            ) {
                return this.selectedLinks.length > 0;
            }
            if (this.selectedType === "productsCarousel") {
                return this.selectedProducts.length > 0;
            }
            if (this.selectedType === "text") {
                if (this.selectedStyle === "titleImage") {
                    return !!this.textImage;
                }
                return !!(this.textTitle.trim() || this.textBody.trim());
            }
            if (this.selectedType === "testimonials") {
                return (
                    this.testimonialItems.length > 0 &&
                    this.testimonialItems.every(
                        (item) => item.name.trim() && item.quote.trim()
                    )
                );
            }
            if (this.selectedType === "media") {
                if (
                    this.socialSupportsMultiple &&
                    this.socialDisplayMode === "multiple"
                ) {
                    return (
                        this.socialItems.length > 0 &&
                        this.socialItems.every((item) =>
                            this.isValidSocialItemLink(item)
                        )
                    );
                }
                return this.socialEmbedValid;
            }
            if (this.selectedType === "markdown") {
                return !!this.markdownContent.trim();
            }
            if (this.selectedType === "divider") {
                return !!this.selectedStyle && !!this.dividerSize;
            }
            return false;
        }
    },
    watch: {
        selectedType(type) {
            if (!type) return;
            this.step = "config";
            this.contentTab = "content";
            if (this.skipNextTypeReset) {
                this.skipNextTypeReset = false;
            } else {
                this.selectedStyle =
                    this.pendingStyle || STYLE_OPTIONS[type]?.[0]?.value || "";
                this.pendingStyle = null;
            }
            if (type === "links" || type === "banners") {
                this.loadExistingLinks();
            }
        }
    },
    mounted() {
        window.addEventListener("resize", this.updateStyleOptionColumnCount);
        this.updateStyleOptionColumnCount();
    },
    beforeDestroy() {
        clearTimeout(this.recentlyMovedTimeout);
        window.removeEventListener("resize", this.updateStyleOptionColumnCount);
    },
    methods: {
        ...mapActions("customLinks", ["listCustomLinks"]),
        ...mapActions("images", ["getImageById"]),
        updateStyleOptionColumnCount() {
            const width = window.innerWidth;
            if (width >= STYLE_OPTION_DESKTOP_BREAKPOINT) {
                this.styleOptionColumnCount = STYLE_OPTION_COLUMNS.desktop;
            } else if (width >= STYLE_OPTION_TABLET_BREAKPOINT) {
                this.styleOptionColumnCount = STYLE_OPTION_COLUMNS.tablet;
            } else {
                this.styleOptionColumnCount = STYLE_OPTION_COLUMNS.mobile;
            }
        },
        // En banners, los estilos se agrupan por nivel de alto (sliderSizeTiers)
        // para que se entienda cuánto ocupa cada uno; el resto de los tipos
        // va en una sola sección sin encabezado.
        buildStyleOptionSections(options) {
            if (!this.isBannerStylePicker) {
                return [
                    {
                        key: "all",
                        label: "",
                        columns: this.splitIntoColumns(
                            options,
                            this.styleOptionColumnCount
                        )
                    }
                ];
            }
            return SLIDER_SIZE_TIER_OPTIONS.map((tier) => ({
                key: tier.value,
                label: tier.label,
                columns: this.splitIntoColumns(
                    options.filter(
                        (option) => sliderSizeTiers[option.value] === tier.value
                    ),
                    this.styleOptionColumnCount
                )
            })).filter((section) => section.columns.some((c) => c.length));
        },
        buildMenuItems(cards) {
            const items = cards.filter((card) => !card.group);
            const hasUiCards = cards.some((card) => card.group === UI_GROUP.id);
            return hasUiCards ? [...items, UI_GROUP] : items;
        },
        filterTypeCards(cards) {
            if (!this.allowedTypes) return cards;
            return cards.filter((card) => this.allowedTypes.includes(card.id));
        },
        splitIntoColumns(options, columnCount = STYLE_OPTION_COLUMNS.mobile) {
            const effectiveColumnCount = Math.max(
                1,
                Math.min(columnCount, options.length)
            );
            const columns = Array.from(
                { length: effectiveColumnCount },
                () => []
            );
            options.forEach((option, index) => {
                columns[index % effectiveColumnCount].push(option);
            });
            return columns;
        },
        previewPropsFor(styleValue) {
            if (this.activeType === "links") {
                const links =
                    styleValue && styleValue !== "grid"
                        ? SAMPLE_LINKS.slice(0, 2)
                        : SAMPLE_LINKS;
                return { links, linkStyle: this.linkStyle };
            }
            if (this.activeType === "banners") {
                const banners =
                    this.bannerSampleMode === "one"
                        ? SAMPLE_BANNERS.slice(0, 1)
                        : SAMPLE_BANNERS;
                return { links: banners, colors: this.colors };
            }
            if (this.activeType === "productsCarousel") {
                return {
                    products: SAMPLE_PRODUCTS,
                    colors: this.colors,
                    stack: this.stack,
                    dark: this.dark
                };
            }
            if (this.activeType === "text") {
                return {
                    title: SAMPLE_TEXT_TITLE,
                    text: SAMPLE_TEXT_BODY,
                    imageUrl:
                        styleValue === "titleImage"
                            ? SAMPLE_TEXT_IMAGE_URL
                            : null,
                    fontFamily: this.selectedFontFamily
                };
            }
            if (this.activeType === "testimonials") {
                return { items: SAMPLE_TESTIMONIAL_ITEMS };
            }
            return {};
        },
        resetAll() {
            this.step = "type";
            this.selectedType = "";
            this.previewType = this.menuItems[0]?.id || "";
            this.pendingStyle = null;
            this.existingLinks = [];
            this.selectedLinks = [];
            this.createCustomLinkModalOpen = false;
            this.selectedProducts = [];
            this.textTitle = "";
            this.textBody = "";
            this.textImage = null;
            this.testimonialItems = [];
            this.markdownContent = "";
            this.dividerSize = "md";
            this.socialPlatformInput = SOCIAL_PLATFORM_OPTIONS[0].value;
            this.socialUrlInput = "";
            this.socialDisplayMode = "single";
            this.socialItems = [];
            this.selectedStyle = "";
            this.styleOrderAnchor = "";
            this.selectedFontFamily = null;
            this.editingSegment = null;
            this.bannerSampleMode = "many";
        },
        chooseTypeAndStyle(type, styleValue) {
            this.pendingStyle = styleValue;
            this.selectedType = type;
        },
        platformIcon(value) {
            return (
                SOCIAL_NETWORK_OPTIONS.find((option) => option.key === value)
                    ?.icon || null
            );
        },
        selectSocialDisplayMode(value) {
            this.socialDisplayMode = value;
        },
        isValidSocialItemLink(item) {
            const detected = detectSocialEmbed(item.link);
            return !!detected && detected.platform === this.socialPlatformInput;
        },
        socialItemError(item) {
            if (!item.link.trim()) return "";
            return this.isValidSocialItemLink(item)
                ? ""
                : `Pega un enlace válido de ${this.socialPlatformLabel}`;
        },
        addSocialItem() {
            this.socialItems = [...this.socialItems, createSocialItem()];
        },
        removeSocialItem(index) {
            this.socialItems = this.socialItems.filter((_, i) => i !== index);
        },
        addTestimonialItem() {
            this.testimonialItems = [
                ...this.testimonialItems,
                createTestimonialItem()
            ];
        },
        removeTestimonialItem(index) {
            this.testimonialItems = this.testimonialItems.filter(
                (_, i) => i !== index
            );
        },
        async hydrateImage(imageId) {
            if (!imageId) return null;
            try {
                return await this.getImageById({ id: imageId });
            } catch {
                return null;
            }
        },
        // Devuelve la promesa del fetch en curso en vez de una nueva cada
        // vez: confirm() la espera antes de guardar (si no, el fetch async
        // puede no haber terminado todavía cuando se guarda el segmento, y
        // queda thumbnailUrl en null para siempre).
        onSocialItemLinkBlur(item) {
            if (!this.isValidSocialItemLink(item)) return Promise.resolve();
            if (item.fetchedForLink === item.link) return Promise.resolve();
            if (item._thumbnailFetchPromise) return item._thumbnailFetchPromise;

            const fetchThumbnail =
                this.socialPlatformInput === "tiktok"
                    ? fetchTiktokThumbnail
                    : fetchInstagramThumbnail;

            item.thumbnailLoading = true;
            item._thumbnailFetchPromise = (async () => {
                try {
                    const result = await fetchThumbnail(item.link);
                    item.thumbnailUrl = result?.thumbnailUrl || null;
                } catch {
                    item.thumbnailUrl = null;
                } finally {
                    item.thumbnailLoading = false;
                    item.fetchedForLink = item.link;
                    item._thumbnailFetchPromise = null;
                }
            })();
            return item._thumbnailFetchPromise;
        },
        selectMenuItem(item) {
            if (item.id === UI_GROUP.id) {
                this.previewType = UI_GROUP.id;
                return;
            }
            this.selectTypeCard(item);
        },
        selectTypeCard(card) {
            this.previewType = card.id;
            if (card.id === "media") return;
            const styles = STYLE_OPTIONS[card.id] || [];
            if (card.presetStyle) {
                this.chooseTypeAndStyle(card.id, card.presetStyle);
                return;
            }
            if (styles.length <= 1) {
                this.chooseTypeAndStyle(card.id, styles[0]?.value || "");
            }
        },
        chooseSocialPlatform(value) {
            this.socialPlatformInput = value;
            this.socialDisplayMode = "single";
            this.socialItems = [];
            this.chooseTypeAndStyle("media", "");
        },
        goBackToType() {
            const hasStyleStep =
                (STYLE_OPTIONS[this.selectedType] || []).length > 1;
            const isUiCard = this.uiGroupCards.some(
                (card) => card.id === this.selectedType
            );
            this.previewType =
                isUiCard && (!hasStyleStep || this.selectedType === "divider")
                    ? UI_GROUP.id
                    : this.selectedType || this.previewType;
            this.step = "type";
            this.selectedType = "";
            this.selectedStyle = "";
        },
        openForAdd() {
            this.resetAll();
            this.isOpen = true;
        },
        // Abre el bloque ya en su configuración y, encima, el formulario
        // para crear el enlace o banner: lo creado queda seleccionado en el
        // bloque (onCustomLinkCreated).
        openForAddWithNewLink(type) {
            this.resetAll();
            const style =
                this.isLinksOnlyMode && type === "banners"
                    ? DEFAULT_SLIDER_VARIANT
                    : STYLE_OPTIONS[type]?.[0]?.value || "";
            this.previewType = type;
            this.chooseTypeAndStyle(type, style);
            this.isOpen = true;
            this.$nextTick(() => this.openCreateLink());
        },
        openForEdit(segment) {
            this.editingSegment = segment;
            this.skipNextTypeReset = true;
            this.selectedType = segment.type;
            this.step = "config";
            this.selectedStyle =
                segment.style || STYLE_OPTIONS[segment.type]?.[0]?.value || "";
            if (
                this.isLinksOnlyMode &&
                segment.type === "banners" &&
                !isLinksOnlyBannerStyle(this.selectedStyle)
            ) {
                this.selectedStyle = DEFAULT_SLIDER_VARIANT;
            }
            this.styleOrderAnchor = this.selectedStyle;
            this.bannerSampleMode =
                segment.type === "banners" && (segment.links || []).length === 1
                    ? "one"
                    : "many";
            this.selectedLinks =
                segment.type === "links" || segment.type === "banners"
                    ? [...(segment.links || [])]
                    : [];
            this.selectedProducts =
                segment.type === "productsCarousel"
                    ? [...(segment.products || [])]
                    : [];
            this.textTitle = segment.type === "text" ? segment.title || "" : "";
            this.textBody = segment.type === "text" ? segment.text || "" : "";
            this.textImage =
                segment.type === "text" ? segment.image || null : null;
            this.testimonialItems =
                segment.type === "testimonials" && segment.items
                    ? segment.items.map((item) => ({
                          ...createTestimonialItem(),
                          ...item,
                          id: item.id || generateSegmentItemId(),
                          image: item.image || null
                      }))
                    : [];
            this.socialPlatformInput =
                segment.type === "media"
                    ? segment.mediaType
                    : SOCIAL_PLATFORM_OPTIONS[0].value;
            this.socialDisplayMode =
                segment.type === "media" && segment.displayMode === "multiple"
                    ? "multiple"
                    : "single";
            this.socialUrlInput =
                segment.type === "media" && this.socialDisplayMode === "single"
                    ? segment.link || ""
                    : "";
            this.socialItems =
                segment.type === "media" && segment.items
                    ? segment.items.map((item) => ({
                          ...createSocialItem(),
                          ...item,
                          id: item.id || generateSegmentItemId()
                      }))
                    : [];
            this.selectedFontFamily =
                segment.type === "text" ? segment.fontFamily || null : null;
            this.markdownContent =
                segment.type === "markdown" ? segment.content || "" : "";
            this.dividerSize =
                segment.type === "divider" ? segment.size || "md" : "md";
            this.isOpen = true;
            // Si el segmento se guardó antes de que existiera el fetch de
            // miniaturas (o falló en su momento), reintenta al reabrir el
            // editor sin que el usuario tenga que volver a pegar el link.
            this.socialItems
                .filter((item) => !item.thumbnailUrl)
                .forEach((item) => this.onSocialItemLinkBlur(item));
            // El segment cargado desde draft.segments nunca pasó por
            // toStoredSegment (solo corre al guardar) — trae imageId suelto
            // salvo que backend lo haya expandido. Se resuelve después de
            // abrir el modal, sin bloquear la apertura (ver D4 de design.md).
            if (
                segment.type === "text" &&
                segment.style === "titleImage" &&
                !this.textImage &&
                segment.imageId
            ) {
                this.hydrateImage(segment.imageId).then((image) => {
                    this.textImage = image;
                });
            }
            this.testimonialItems
                .filter((item) => !item.image && item.imageId)
                .forEach((item) => {
                    this.hydrateImage(item.imageId).then((image) => {
                        item.image = image;
                    });
                });
        },
        async loadExistingLinks() {
            this.isLoadingLinks = true;
            try {
                const links = await this.listCustomLinks({
                    type: this.selectedType === "banners" ? "banner" : "link"
                });
                this.existingLinks = links.map((link) => ({
                    id: link.id,
                    title: link.title,
                    url: link.url,
                    entity: link.entity,
                    image: link.image ?? null,
                    imageUrl: link.image?.url ?? null
                }));
                this.isLoadingLinks = false;
            } catch (error) {
                this.isLoadingLinks = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        onToggleExistingLink(link, ids) {
            if (ids.includes(link.id)) {
                this.selectedLinks = [...this.selectedLinks, link];
            } else {
                this.selectedLinks = this.selectedLinks.filter(
                    (item) => item.id !== link.id
                );
            }
        },
        onSelectedLinkDragEnd({ newIndex, oldIndex }) {
            if (oldIndex === newIndex) return;

            const moved = this.selectedLinks[newIndex];
            if (!moved) return;

            this.recentlyMovedLinkId = moved.id;
            clearTimeout(this.recentlyMovedTimeout);
            this.recentlyMovedTimeout = setTimeout(() => {
                this.recentlyMovedLinkId = null;
            }, 600);
        },
        openCreateLink() {
            this.editingLink = null;
            this.createCustomLinkModalOpen = true;
        },
        openEditLink(link) {
            this.editingLink = link;
            this.createCustomLinkModalOpen = true;
        },
        onCustomLinkCreated(link) {
            if (!link) return;
            this.existingLinks = [link, ...this.existingLinks];
            this.selectedLinks = [...this.selectedLinks, link];
        },
        onCustomLinkUpdated(updated) {
            if (!updated) return;
            this.existingLinks = this.existingLinks.map((link) =>
                link.id === updated.id ? { ...link, ...updated } : link
            );
            this.selectedLinks = this.selectedLinks.map((link) =>
                link.id === updated.id ? { ...link, ...updated } : link
            );
        },
        onProductSelected(product) {
            if (!product?.slug) return;
            if (this.selectedProducts.some((item) => item.id === product.id))
                return;
            this.selectedProducts = [...this.selectedProducts, product];
        },
        productImageUrl(product) {
            return product.references?.[0]?.images?.[0]?.url;
        },
        removeSelectedProduct(product) {
            this.selectedProducts = this.selectedProducts.filter(
                (item) => item.id !== product.id
            );
        },
        onSelectedProductDragEnd({ newIndex, oldIndex }) {
            if (oldIndex === newIndex) return;

            const moved = this.selectedProducts[newIndex];
            if (!moved) return;

            this.recentlyMovedProductId = moved.id;
            clearTimeout(this.recentlyMovedTimeout);
            this.recentlyMovedTimeout = setTimeout(() => {
                this.recentlyMovedProductId = null;
            }, 600);
        },
        onProductCreated(product) {
            if (!product) return;
            this.selectedProducts = [
                ...this.selectedProducts,
                {
                    ...product,
                    priceOffer: product.priceOffer ?? 0
                }
            ];
        },
        async confirm() {
            if (!this.canConfirm) return;

            let segment = null;
            if (
                this.selectedType === "links" ||
                this.selectedType === "banners"
            ) {
                segment = {
                    type: this.selectedType,
                    style: this.selectedStyle,
                    links: this.selectedLinks
                };
            } else if (this.selectedType === "productsCarousel") {
                segment = {
                    type: "productsCarousel",
                    style: this.selectedStyle,
                    products: this.selectedProducts
                };
            } else if (this.selectedType === "text") {
                segment = {
                    type: "text",
                    style: this.selectedStyle,
                    title: this.textTitle.trim() || undefined,
                    text: this.textBody.trim() || undefined,
                    fontFamily: this.selectedFontFamily || undefined
                };
                if (this.selectedStyle === "titleImage") {
                    segment.image = this.textImage;
                }
            } else if (this.selectedType === "testimonials") {
                segment = {
                    type: "testimonials",
                    style: "carousel",
                    items: this.testimonialItems.map((item) => ({
                        id: item.id,
                        name: item.name.trim(),
                        quote: item.quote.trim(),
                        image: item.image || null
                    }))
                };
            } else if (this.selectedType === "media") {
                const isMultiple =
                    this.socialSupportsMultiple &&
                    this.socialDisplayMode === "multiple";
                segment = {
                    type: "media",
                    style: "embed",
                    mediaType: this.socialPlatformInput,
                    displayMode: isMultiple ? "multiple" : "single"
                };
                if (isMultiple) {
                    // Espera cualquier fetch de miniatura en curso (se
                    // dispara en @blur del link) antes de guardar — si no,
                    // el fetch async puede no haber terminado todavía y el
                    // segmento se guarda con thumbnailUrl en null.
                    await this.$loading.wrap(() =>
                        Promise.all(
                            this.socialItems.map((item) =>
                                this.onSocialItemLinkBlur(item)
                            )
                        )
                    );
                    segment.items = this.socialItems.map(
                        ({ id, link, thumbnailUrl }) => ({
                            id,
                            link: link.trim(),
                            thumbnailUrl
                        })
                    );
                } else {
                    segment.link = this.socialUrlInput.trim();
                }
            } else if (this.selectedType === "markdown") {
                segment = {
                    type: "markdown",
                    content: this.markdownContent.trim()
                };
            } else if (this.selectedType === "divider") {
                segment = {
                    type: "divider",
                    style: this.selectedStyle,
                    size: this.dividerSize
                };
            }

            if (!segment) return;
            segment.id = this.editingSegment?.id || generateSegmentItemId();
            this.$emit("confirm", segment);
            this.isOpen = false;
        }
    }
};
</script>
